/* eslint-disable array-callback-return */
import {
  Box,
  Chip,
  DeleteIcon,
  Divider,
  Edit1Icon,
  GKButton,
  Grid2,
  IconButton,
  RiskProfileIcon,
  StatusField,
  Tab,
  Tabs,
  TermIcon,
  Typography,
  VolatileIcon,
} from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { SingleStockItemInterface } from "interfaces/EquityBasket";
import DashboardLayout from "layouts/DashboardLayout";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { TAB } from "store/slices/appSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { handleRemoveUnderscore, numberToCurrency } from "utils";
import axiosInstance from "utils/axiosInstance";
import { handleCapitalize } from "utils/common";
import useDocumentTitle from "utils/useDocumentTitle";
import AssetsStatusModal from "../EquityBasketAssets/AssetsStatusModal";
import EquityBasketRecommendationHistory from "./EquityBasketRecommendationHistory";
import RationaleTimeline from "./RationaleTimeline";

const RecommendationView = () => {
  const navigate = useNavigate();

  useDocumentTitle("Basket Asset Details");

  const dispatch = useAppDispatch();

  const { assetDetailsTabs } = useAppSelector((state) => state?.app?.tab);

  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);

  const { id, assetId } = useParams();

  const form = useForm({
    defaultValues: {
      stockName: "",
      recommendedBuyPrice: "",
      rationale: "",
      status: "",
      riskProfile: "",
      published: false,
      timePeriod: "",
      volatility: "",
      targetPrice: "",
      limitPrice: "",
      deviation: "",
      stopLoss: "",
      research_files: [],
    },
  });

  const {
    data: addDetailsData,
    isFetching,
    isRefetching,
  } = useFetchQuery<{ data: SingleStockItemInterface }>({
    key: ["GET_STOCK_DETAILS", id, assetId],
    route: `/model_portfolio/${id}/stock_item/${assetId}/`,
    success: (res: any) => {
      form.reset({
        ...res?.data,
        research_files: [],
      });
    },
  });

  const tabsData = [
    {
      show: true,
      title: "Recommendations History",
      element: (
        <EquityBasketRecommendationHistory
          stockRecommendations={addDetailsData?.data?.stockRecommendations}
        />
      ),
    },
    {
      show: true,
      title: "Rational Timeline",
      element: (
        <RationaleTimeline
          rationaleTimeline={
            addDetailsData?.data?.stockUniverseItemHistory as any
          }
          researchFiles={addDetailsData?.data?.researchFiles}
        />
      ),
    },
  ];

  /** ========================================================================================================= */
  const { mutate: handleDeleteStock } = useMutation({
    mutationFn: (data: { modelId: any; stockItemId: any }) =>
      axiosInstance?.delete(
        `/model_portfolio/${data?.modelId}/stock_item/${data?.stockItemId}/`,
      ),
    onSuccess: () => {
      navigate(-1);
    },
  });

  return (
    <>
      <DashboardLayout
        title="Assets Details"
        loading={isFetching && !isRefetching}
      >
        <Grid2 container spacing={1}>
          <Grid2
            container
            spacing={1}
            bgcolor={"#F8F8F8"}
            size={12}
            borderRadius={2}
            p={2}
          >
            <Grid2 size={3} display={"flex"} alignItems={"center"}>
              <StatusField statusString={addDetailsData?.data?.assetStatus} />
            </Grid2>
            <Grid2
              size={9}
              textAlign={"right"}
              gap={1}
              display={"flex"}
              justifyContent={"flex-end"}
              alignItems={"center"}
            >
              <IconButton
                color="error"
                onClick={() => {
                  handleDeleteStock({
                    modelId: id,
                    stockItemId: assetId,
                  });
                }}
              >
                <DeleteIcon />
              </IconButton>
              <Divider orientation="vertical" />
              <GKButton
                sx={{ p: 0 }}
                size="small"
                onClick={() => {
                  navigate(`/app/equity-basket/${id}/assets/${assetId}/edit`);
                }}
                startIcon={<Edit1Icon />}
              >
                Edit Details
              </GKButton>
            </Grid2>
            <Grid2 size={12}>
              <Typography fontWeight={600} fontSize={24}>
                {addDetailsData?.data?.companyName}
              </Typography>
              <Typography>
                CMP:
                <span
                  style={{
                    fontWeight: 600,
                  }}
                >
                  {numberToCurrency(addDetailsData?.data?.cmp)}
                </span>
              </Typography>
            </Grid2>
            {addDetailsData?.data?.riskProfile[0] && (
              <Grid2 size={"auto"}>
                <Chip
                  sx={{
                    bgcolor: "var(--white)",
                    borderRadius: 1,
                  }}
                  variant="outlined"
                  label={
                    <Box display={"flex"} alignItems={"center"} gap={1}>
                      <RiskProfileIcon color={`var(--warning-700)`} />
                      {addDetailsData?.data?.riskProfile[0]}
                    </Box>
                  }
                />
              </Grid2>
            )}
            {addDetailsData?.data?.volatility && (
              <Grid2 size={"auto"}>
                <Chip
                  sx={{
                    bgcolor: "var(--white)",
                    borderRadius: 1,
                  }}
                  variant="outlined"
                  label={
                    <Box display={"flex"} alignItems={"center"} gap={1}>
                      <VolatileIcon color={"var(--orange-700)"} />
                      {handleCapitalize(addDetailsData?.data?.volatility)}{" "}
                      volatility
                    </Box>
                  }
                />
              </Grid2>
            )}
            {addDetailsData?.data?.timePeriod && (
              <Grid2 size={"auto"}>
                <Chip
                  sx={{
                    bgcolor: "var(--white)",
                    borderRadius: 1,
                  }}
                  variant="outlined"
                  label={
                    <Box display={"flex"} alignItems={"center"} gap={1}>
                      <TermIcon color={"var(--blue-700)"} />
                      {handleRemoveUnderscore(
                        handleCapitalize(addDetailsData?.data?.timePeriod),
                      )}
                    </Box>
                  }
                />
              </Grid2>
            )}
          </Grid2>
          <Grid2 size={12}>
            <Tabs
              onChange={(e, val) => {
                dispatch(TAB({ tabKey: "assetDetailsTabs", tabValue: val }));
              }}
              value={assetDetailsTabs}
            >
              {tabsData.map(
                (daItem) => daItem.show && <Tab label={daItem.title} />,
              )}
            </Tabs>
            <Divider />
          </Grid2>
          <Grid2 size={12}>{tabsData[assetDetailsTabs].element}</Grid2>
        </Grid2>
      </DashboardLayout>
      {isStatusModalOpen && (
        <AssetsStatusModal
          open={isStatusModalOpen}
          setOpen={setIsStatusModalOpen}
        />
      )}
    </>
  );
};

export default RecommendationView;
